import { useEffect, useRef, useState } from 'react'

import AccountCircle from '@mui/icons-material/AccountCircle'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ErrorIcon from '@mui/icons-material/ErrorOutlined'
import MenuIcon from '@mui/icons-material/Menu'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import { useAppbarTitleStore } from 'Common/store/AppBarTitleStore'
import { useMainNavStore } from 'Common/store/MainNavStore'
import { useMobileTopNavStore } from 'Common/store/MobileTopNavStore'
import { useErrorStore } from 'Common/store/useErrorStore'
import { useAuth } from 'Common/utils/query/useAuth'
import { isOpusAdmin } from 'Common/utils/user/auth'

import { RouterMenuItem } from '../links/routerLinks'
import { ErrorListDialog } from './ErrorListDialog'

const drawerWidth = 240

type Props = {
    logout: () => void
}

export default function PrimaryAppBar(props: Readonly<Props>) {
    const { logout } = props
    const auth = useAuth()
    const actionMenuAnchor = useRef<HTMLSpanElement | null>(null)
    const [showMenu, setShowMenu] = useState(false)
    const [showErrorList, setShowErrorList] = useState(false)
    const { hasMobileTopNav, showMobileTopNav, setShowMobileTopNav } = useMobileTopNavStore()
    const { openMainNav, setOpenMainNav } = useMainNavStore()
    const theme = useTheme()
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'))

    useEffect(() => {
        if (!isMdUp && openMainNav) {
            setOpenMainNav(false)
        }
    }, [isMdUp, openMainNav, setOpenMainNav])

    const displayName =
        [auth?.data?.given_name, auth?.data?.family_name].filter(Boolean).join(' ') ||
        (auth?.data?.name ?? '')
    const opusUserId = auth?.data?.opus_user_id ?? ''
    const title = useAppbarTitleStore((state) => state.appTitle)
    const errors = useErrorStore((s) => s.errors)

    return (
        <>
            <AppBar
                position="fixed"
                sx={{
                    display: 'flex',
                    displayPrint: 'none',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    transition: (theme) =>
                        theme.transitions.create(['width', 'margin'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                    ...(openMainNav && {
                        [theme.breakpoints.up('md')]: {
                            marginLeft: `${drawerWidth}px`,
                            width: `calc(100% - ${drawerWidth - 10}px)`,
                            transition: (theme) =>
                                theme.transitions.create(['width', 'margin'], {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.enteringScreen,
                                }),
                        },
                    }),
                }}
            >
                <Toolbar disableGutters sx={{ pr: 4 }}>
                    {isMdUp && !openMainNav ? (
                        <IconButton
                            disableRipple
                            color="inherit"
                            aria-label="Open drawer"
                            onClick={() => setOpenMainNav(!openMainNav)}
                            size="large"
                        >
                            <ChevronRightIcon fontSize="large" />
                        </IconButton>
                    ) : null}
                    {hasMobileTopNav ? (
                        <IconButton
                            disableRipple
                            color="inherit"
                            aria-label="Open sub menu"
                            onClick={() => setShowMobileTopNav(!showMobileTopNav)}
                            size="large"
                        >
                            <MenuIcon fontSize="large" />
                        </IconButton>
                    ) : null}
                    <Typography
                        variant="h5"
                        sx={{
                            color: 'inherit',
                            flexGrow: 1,
                            pl: hasMobileTopNav || openMainNav ? 2 : 1,
                        }}
                    >
                        {title}
                    </Typography>
                    {errors.length > 0 && (
                        <IconButton color="inherit" onClick={() => setShowErrorList(true)}>
                            <ErrorIcon fontSize="large" />
                        </IconButton>
                    )}
                    <Box ref={actionMenuAnchor}>
                        <IconButton
                            disableRipple
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={() => setShowMenu(true)}
                            color="inherit"
                        >
                            <AccountCircle fontSize="large" />
                        </IconButton>
                        <Menu
                            anchorEl={actionMenuAnchor.current}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'center', vertical: 'center' }}
                            keepMounted
                            open={showMenu}
                            onClose={() => setShowMenu(false)}
                        >
                            <RouterMenuItem
                                disabled={!isOpusAdmin(auth)}
                                nativeButton={false}
                                onClick={() => setShowMenu(false)}
                                to="/user/$userId"
                                params={{ userId: String(opusUserId) }}
                            >
                                {displayName}
                            </RouterMenuItem>
                            {isOpusAdmin(auth) && <Divider />}
                            <MenuItem onClick={logout}>Logout</MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            {showErrorList && <ErrorListDialog onClose={() => setShowErrorList(false)} />}
        </>
    )
}
